/**
 * `useNav` (routing-and-state.md §5). Holds the sidebar collapsed/expanded preference
 * (desktop rail) and the transient mobile-drawer open flag. The collapsed preference is
 * persisted to **localStorage** (not sessionStorage) so the choice survives a tab close
 * (F-227 2026-06-04); the drawer flag is in-memory only (it must never open on reload).
 */
import { create } from 'zustand';

const STORAGE_KEY = 'argus.nav.sidebarCollapsed';

function readInitial(): boolean {
  try {
    return globalThis.localStorage?.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function persist(collapsed: boolean): void {
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, collapsed ? '1' : '0');
  } catch {
    /* ignore — private-mode / disabled storage degrades to in-memory only */
  }
}

interface NavState {
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  /** The < 900px off-canvas drawer. */
  mobileOpen: boolean;
  setMobileOpen: (open: boolean) => void;
}

export const useNav = create<NavState>((set, get) => ({
  sidebarCollapsed: readInitial(),
  toggleSidebar: () => {
    const next = !get().sidebarCollapsed;
    persist(next);
    set({ sidebarCollapsed: next });
  },
  setSidebarCollapsed: (collapsed) => {
    persist(collapsed);
    set({ sidebarCollapsed: collapsed });
  },
  mobileOpen: false,
  setMobileOpen: (open) => set({ mobileOpen: open }),
}));
